/* eslint-disable no-case-declarations */
/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import clsx from 'clsx';
import { PredictManagementContext } from '../../../predict-management/context/predict-management-context';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { useIntl } from 'react-intl';

import { ForecastListItem } from '@futrli-sdk/predict';
import { UILoader } from '@futrli/ui-components';

import { useReportBuilderContext } from '../../report-builder.context';
import { LayoutItem as Layout } from '../grid-layout';
import {
  CARD_TYPES,
  DataField,
  LayoutData,
  WidgetData,
  WIDGET_TYPES,
  FinData,
  FinFieldOptions,
  CalendarOptions,
  PeriodType,
  GROUPING_OPTIONS
} from '../../types';
import { totalVarianceRef, LAYOUT_STEP, newDefaultRowStyles } from '../../constants';
import { DataCard } from '../data-card/data-card';
import {
  flattenFinFieldsToRefs,
  getConsolidationShortCode,
  getFieldsByGroupingWithLevel,
  getFieldWithChildren,
  getPeriodsFromComparisonFieldMeta,
  getWidgetData,
  localizeCompData,
  localizeFinData,
  widgetHasTwoAxes
} from '../../utils-data';
import { inchesToPX } from '../../utils-layout';
import { EditableTitles } from '../editable-titles/editable-titles';
import { useConsolidatedForecastingContext } from '../../../forecast/detail/context/consolidated-forecasting-context';
import { ReportBarLineChart } from '../report-charts/report-bar-line-chart';
import { ReportPieDonutChart } from '../report-charts/report-pie-donut-chart';
import { PaginatedReportTable } from '../report-table/paginated-report-table';
import { useI18nConstants } from '../../use-i18n-constants';

import styles from './widget-card.scss';

interface Props {
  cardData: LayoutData;
  layoutInfo: Layout;
  viewOnly?: boolean;
  currency?: string;
}
export const WidgetCard = ({ cardData, layoutInfo, viewOnly, currency: propsCurrency }: Props) => {
  const intl = useIntl();
  const { currency: contextCurrency } = useContext(PredictManagementContext);
  const { consolidation } = useConsolidatedForecastingContext();
  const { actualForecasts, datasetComparisonChildren, dummyTemplateForecasts } = useI18nConstants();
  const currency = contextCurrency || propsCurrency || '';
  const [widgetOptions, setWidgetOptions] = useState<WidgetData>(cardData[CARD_TYPES.WIDGET] as WidgetData);
  const [finFieldsOptions, setFinFieldsOptions] = useState<{ ref: string; options: FinFieldOptions | undefined }[]>(
    cardData[CARD_TYPES.WIDGET]?.finFieldsOptions || []
  );
  const [calFieldOptions, setCalFieldOptions] = useState<CalendarOptions | undefined>(
    cardData[CARD_TYPES.WIDGET]?.calendarOptions
  );
  const titlesSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const newWidgetOptions = cardData[CARD_TYPES.WIDGET] as WidgetData;
    if (!isEqual(JSON.parse(JSON.stringify(widgetOptions)), JSON.parse(JSON.stringify(newWidgetOptions)))) {
      setWidgetOptions(newWidgetOptions);
      setFinFieldsOptions(newWidgetOptions.finFieldsOptions);
      setCalFieldOptions(newWidgetOptions.calendarOptions);
    }
  }, [cardData, widgetOptions]);

  const {
    layoutFinData,
    layoutComparisonData,
    pageSetup: { margins, orientation },
    headerFooterLimits,
    availableFinFields,
    allFinFields,
    isTemplate,
    isWorkspaceTemplate,
    getPageHeight,
    allFinFieldsArray,
    currentCardId,
    setCollapsedFields,
    widgetsAwaitingData
  } = useReportBuilderContext();
  const isFutrliTemplate = isTemplate && !isWorkspaceTemplate;
  const selectedVariances = widgetOptions.selectedVariances;

  const data = useMemo(
    () =>
      localizeFinData(
        getWidgetData(
          Object.keys(layoutFinData[cardData.i] || {}).map(f => layoutFinData[cardData.i]?.[f]),
          selectedVariances,
          widgetOptions?.widgetType as WIDGET_TYPES | undefined
        ),
        intl
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layoutFinData, cardData.i]
  );
  const { selectedDatasets } = widgetOptions;

  const shouldUseComparisonData = !!(
    (selectedDatasets?.length || 0) > 1 &&
    widgetOptions.widgetType === WIDGET_TYPES.TABLE &&
    selectedVariances.find(v => datasetComparisonChildren.find(dc => v.ref === dc.ref)) &&
    layoutComparisonData?.[cardData.i]
  );

  const compData = useMemo(() => {
    if (!shouldUseComparisonData) {
      return undefined;
    }
    const comparisonData = layoutComparisonData[cardData.i];
    const periods = getPeriodsFromComparisonFieldMeta(
      (comparisonData?.field_meta as unknown) as Record<
        string,
        { column_meta: { period: string; is_first_in_period: boolean }[] }
      >
    );
    if (selectedVariances.find(e => e.ref === totalVarianceRef) && !periods.find(p => p === 'Total')) {
      periods.push('Total');
    }
    return localizeCompData([{ ...comparisonData, periods } as FinData], intl);
  }, [shouldUseComparisonData, layoutComparisonData, cardData.i, selectedVariances, intl]);

  const { forecasts } = useReportBuilderContext();

  const selectedForecasts = useMemo(
    () =>
      (selectedDatasets || [])
        .map(
          sd =>
            forecasts.find(f => f.id === sd) ||
            actualForecasts.find(f => f.id === sd) ||
            (isFutrliTemplate && dummyTemplateForecasts.find(f => f.id === sd))
        )
        .filter(f => !!f) as ForecastListItem[],
    [forecasts, actualForecasts, selectedDatasets, isFutrliTemplate, dummyTemplateForecasts]
  );

  const fieldsIncChildren = useMemo(() => {
    let fwc: {
      ref: string;
      name: string;
      level: number;
      classifier: string;
      isSecondary: boolean;
      organisation_id?: string;
    }[] = [];
    const assumptionsFields: { ref: string; name: string; parentRef: string }[] = [];
    let assumptionsProcessed = false;
    (widgetOptions.selectedFinFields || []).forEach(f => {
      if (f.classifier === 'row-formula' || f.classifier === 'template-formula') {
        fwc.push({ ref: f.ref, name: f.name, level: 0, classifier: f.classifier, isSecondary: !!f.isSecondary });
      } else if (!f.isSecondary || widgetHasTwoAxes(widgetOptions.widgetType as WIDGET_TYPES)) {
        const ffc = getFieldWithChildren(allFinFields, f.ref, f.isSecondary) || f;
        const fieldOptions = (widgetOptions.finFieldsOptions || []).find(e => e.ref === f.ref);
        if ((fieldOptions?.options?.grouping || []).includes(GROUPING_OPTIONS.ASSUMPTIONS)) {
          // adding assumptions
          if (!assumptionsProcessed) {
            assumptionsProcessed = true;
            Object.keys(layoutFinData[cardData.i] || {}).forEach(dsKey => {
              const fieldMeta = layoutFinData[cardData.i]?.[dsKey]?.field_meta || {};
              Object.keys(fieldMeta).forEach(fieldKey => {
                const dsField = ((fieldMeta as unknown) as Record<
                  string,
                  { assumption_meta: { name: string; parent_ref: string } }
                >)[fieldKey];

                if (dsField.assumption_meta) {
                  assumptionsFields.push({
                    ref: fieldKey,
                    name: dsField.assumption_meta.name,
                    parentRef: dsField.assumption_meta.parent_ref
                  });
                }
              });
            });
          }
        }
        const widgetType = widgetOptions.widgetType;
        const isSingleGroupingWidget =
          widgetType === WIDGET_TYPES.PIE || widgetType === WIDGET_TYPES.DONUT || widgetType === WIDGET_TYPES.DATA_CARD;
        const grouping = fieldOptions?.options?.grouping || [];
        const fieldWithChildrenByGrouping = getFieldsByGroupingWithLevel(
          ffc,
          isSingleGroupingWidget && grouping.length > 1 ? [grouping[0]] : grouping,
          !!f.isSecondary,
          assumptionsFields,
          fieldOptions?.options?.excludedChildren,
          fieldOptions?.options?.includedChildren
        );
        fwc = fwc.concat(fieldWithChildrenByGrouping);
      }
    });
    // add -CF suffix to cashflow fields if we have cashflow fields and non cashflow fields in the same list
    // we should exclude assumption as those are not part of the available line data
    const flattenedCF = flattenFinFieldsToRefs(availableFinFields.cash_flow as DataField[]);
    const hasCFFields = fwc.some(f => flattenedCF.includes(f.ref));
    const hasNonCFFields = fwc.some(f => f.classifier !== 'assumption' && !flattenedCF.includes(f.ref));
    if (hasCFFields && hasNonCFFields) {
      for (const f of fwc) {
        if (flattenedCF.includes(f.ref) && !f.name.includes('-CF')) {
          f.name = `${f.name}-CF`;
        }
      }
    }

    // add consolidation short code
    if (consolidation) {
      fwc = fwc.map(f =>
        f.organisation_id
          ? { ...f, name: `${f.name} - ${getConsolidationShortCode(consolidation, f.organisation_id)}` }
          : f
      );
    }
    return fwc;
  }, [availableFinFields, allFinFields, widgetOptions, layoutFinData, cardData.i, consolidation]);

  const rowsOptions = useMemo(() => {
    if (widgetOptions.widgetType !== WIDGET_TYPES.TABLE) {
      return [];
    }
    const newOptions: { ref: string; options: FinFieldOptions | undefined }[] = [];
    finFieldsOptions.forEach(option => {
      newOptions.push(option);
      const fwc = (allFinFieldsArray || []).find(field => field.ref === option.ref);
      if (fwc) {
        const children = getFieldsByGroupingWithLevel(
          fwc,
          option.options?.grouping || [],
          false,
          [],
          option.options?.excludedChildren,
          option.options?.includedChildren
        ).map(e => e.ref);
        const childrenOptions = children.map(child => ({ ref: child, options: option.options }));
        newOptions.push(...childrenOptions);
      }
    });
    return newOptions;
  }, [widgetOptions.widgetType, finFieldsOptions, allFinFieldsArray]);

  const titlesHeight = titlesSectionRef.current?.getBoundingClientRect().height || 0;

  useEffect(() => {
    // when user drags a top level group of categories, and sets its options, we needs to pass those options to the top level children
    const PLOptions = finFieldsOptions.find(op => op.ref === 'profit_and_loss');
    const BSOptions = finFieldsOptions.find(op => op.ref === 'balance_sheet');
    const CFOptions = finFieldsOptions.find(op => op.ref === 'cash_flow');
    const flattenedCF = flattenFinFieldsToRefs(availableFinFields.cash_flow as DataField[]);
    const flattenedPL = flattenFinFieldsToRefs(availableFinFields.profit_and_loss as DataField[]);
    const flattenedBS = flattenFinFieldsToRefs(availableFinFields.balance_sheet as DataField[]);
    const optionsToAdd: { ref: string; options: FinFieldOptions | undefined }[] = [];
    fieldsIncChildren
      .filter(f => f.level === 0)
      .forEach(field => {
        if (finFieldsOptions.find(f => f.ref === field.ref)) {
          return;
        }
        if (PLOptions && flattenedPL.find(f => f === field.ref)) {
          optionsToAdd.push({ ref: field.ref, options: PLOptions.options });
        }
        if (BSOptions && flattenedBS.find(f => f === field.ref)) {
          optionsToAdd.push({ ref: field.ref, options: BSOptions.options });
        }
        if (CFOptions && flattenedCF.find(f => f === field.ref)) {
          optionsToAdd.push({ ref: field.ref, options: CFOptions.options });
        }
      });
    if (optionsToAdd.length) {
      setFinFieldsOptions([...finFieldsOptions, ...optionsToAdd]);
    }
  }, [fieldsIncChildren, finFieldsOptions, availableFinFields]);

  const showLoader = useMemo(() => widgetsAwaitingData.includes(cardData.i), [widgetsAwaitingData, cardData.i]);

  const renderChart = () => {
    switch (widgetOptions.widgetType) {
      case WIDGET_TYPES.PIE:
      case WIDGET_TYPES.DONUT:
        return (
          <ReportPieDonutChart
            data={data}
            fields={fieldsIncChildren as DataField[]}
            finFieldsOptions={finFieldsOptions}
            primaryAxisOptions={widgetOptions.primaryAxisOptions}
            secondaryAxisOptions={widgetOptions.secondaryAxisOptions}
            currency={currency || ''}
            selectedForecasts={selectedForecasts}
            isDonut={widgetOptions.widgetType === WIDGET_TYPES.DONUT}
          />
        );
      case WIDGET_TYPES.TABLE:
        let nextLimit = 0;
        headerFooterLimits.current.some(l => {
          nextLimit = l[0];
          return l[0] > layoutInfo.y;
        });
        // since page limit (where you can place widgets) does not necessarily align with the natural end of page
        // we calculate the remaining left over to pass for initial space
        const leftOver =
          Math.round((nextLimit * LAYOUT_STEP) / getPageHeight()) * getPageHeight() -
          inchesToPX(margins[0].value) -
          inchesToPX(margins[2].value) -
          nextLimit * LAYOUT_STEP;
        return (
          <PaginatedReportTable
            data={shouldUseComparisonData && compData ? compData : data}
            hasComparisonData={!!(shouldUseComparisonData && compData)}
            fields={fieldsIncChildren}
            rowsOptions={rowsOptions}
            headerRowStyle={widgetOptions.headerRow || newDefaultRowStyles.headerRow}
            forecastsRowStyle={widgetOptions.forecastsRow || newDefaultRowStyles.forecastsRow}
            normalRowStyle={widgetOptions.normalRow || newDefaultRowStyles.normalRow}
            categoriesRowStyle={
              widgetOptions.categoriesRow ||
              widgetOptions.customRow?.find(e => e.type === 'categories')?.style ||
              newDefaultRowStyles.categoriesRow
            }
            groupsRowStyle={
              widgetOptions.groupsRow ||
              widgetOptions.customRow?.find(e => e.type === 'groups')?.style ||
              newDefaultRowStyles.groupsRow
            }
            accountsRowStyle={
              widgetOptions.accountsRow ||
              widgetOptions.customRow?.find(e => e.type === 'accounts')?.style ||
              newDefaultRowStyles.accountsRow
            }
            assumptionsRowStyle={
              widgetOptions.assumptionsRow ||
              widgetOptions.customRow?.find(e => e.type === 'assumptions')?.style ||
              newDefaultRowStyles.assumptionsRow
            }
            formulasRowStyle={
              widgetOptions.formulasRow ||
              widgetOptions.customRow?.find(e => e.type === 'formulas')?.style ||
              newDefaultRowStyles.formulasRow
            }
            driverGroupsRowStyle={widgetOptions.driverGroupsRow || newDefaultRowStyles.driverGroupsRow}
            driversRowStyle={widgetOptions.driversRow || newDefaultRowStyles.driversRow}
            currency={currency || ''}
            initialSpace={(nextLimit - layoutInfo.y) * LAYOUT_STEP + leftOver}
            maxSpace={getPageHeight() - inchesToPX(margins[0].value) - inchesToPX(margins[2].value)}
            title={widgetOptions.widgetTitle || ''}
            subTitle={widgetOptions.widgetSubTitle || ''}
            breakSpace={
              Math.ceil((inchesToPX(margins[0].value) + inchesToPX(margins[2].value)) / LAYOUT_STEP) * LAYOUT_STEP
            }
            doNotBreak={orientation === 'dashboard'}
            selectedForecasts={selectedForecasts}
            tableWidth={layoutInfo.w}
            periodType={(calFieldOptions?.type as PeriodType) || 'monthly'}
            allFinFieldsArray={allFinFieldsArray}
            shouldPaginate={widgetOptions.tableShouldPaginate}
            firstColumnWidth={widgetOptions.tableFirstColumnWidth}
            columnWidth={widgetOptions.tableColumnWidth}
            userZoom={widgetOptions.tableZoom}
            collapsedFields={widgetOptions.collapsedFields}
            updateCollapsedFields={setCollapsedFields}
            viewOnly={viewOnly}
          />
        );
      case WIDGET_TYPES.DATA_CARD:
        return (
          <DataCard
            title={widgetOptions.widgetTitle || ''}
            data={data}
            fields={widgetOptions.selectedFinFields}
            primaryAxisOptions={widgetOptions.primaryAxisOptions}
            currency={currency || ''}
            selectedForecasts={selectedForecasts}
            viewOnly={viewOnly}
          />
        );
      case WIDGET_TYPES.LINE:
      case WIDGET_TYPES.LINE_HORIZONTAL:
      case WIDGET_TYPES.AREA:
        return (
          <ReportBarLineChart
            data={data}
            fields={fieldsIncChildren as DataField[]}
            finFieldsOptions={finFieldsOptions}
            primaryAxisOptions={widgetOptions.primaryAxisOptions}
            secondaryAxisOptions={widgetOptions.secondaryAxisOptions}
            currency={currency || ''}
            selectedForecasts={selectedForecasts}
            widgetType={widgetOptions.widgetType}
            showZeroLine={widgetOptions.showZeroLine}
            showForecastStartLine={widgetOptions.showForecastStartLine}
            forecastStartLineColors={widgetOptions.forecastStartLineColors}
            showBarLabels={widgetOptions.showBarLabels}
            layoutWidth={layoutInfo.w}
            layoutHeight={layoutInfo.h}
            periodType={(calFieldOptions?.type as PeriodType) || 'monthly'}
          />
        );
      default:
        return (
          <ReportBarLineChart
            data={data}
            fields={fieldsIncChildren as DataField[]}
            finFieldsOptions={finFieldsOptions}
            primaryAxisOptions={widgetOptions.primaryAxisOptions}
            secondaryAxisOptions={widgetOptions.secondaryAxisOptions}
            currency={currency || ''}
            selectedForecasts={selectedForecasts}
            widgetType={widgetOptions.widgetType as WIDGET_TYPES}
            showZeroLine={widgetOptions.showZeroLine}
            showForecastStartLine={widgetOptions.showForecastStartLine}
            forecastStartLineColors={widgetOptions.forecastStartLineColors}
            showBarLabels={widgetOptions.showBarLabels}
            layoutWidth={layoutInfo.w}
            layoutHeight={layoutInfo.h}
            periodType={(calFieldOptions?.type as PeriodType) || 'monthly'}
          />
        );
    }
  };

  return (
    <div className={clsx(styles.widgetCard)}>
      {widgetOptions.widgetType !== WIDGET_TYPES.DATA_CARD && widgetOptions.widgetType !== WIDGET_TYPES.TABLE && (
        <div ref={titlesSectionRef}>
          {!viewOnly ? (
            <EditableTitles
              title={widgetOptions.widgetTitle || ''}
              subTitle={widgetOptions.widgetSubTitle || ''}
              viewOnly={!!viewOnly}
            />
          ) : (
            <>
              <h3>{widgetOptions.widgetTitle || ''}</h3>
              <p>{widgetOptions.widgetSubTitle || ''}</p>
            </>
          )}
        </div>
      )}
      <div
        className={clsx(
          widgetOptions.widgetType !== WIDGET_TYPES.DATA_CARD && widgetOptions.widgetType !== WIDGET_TYPES.TABLE
            ? styles.chartContainer
            : styles.dataCardContainer
        )}
        style={
          widgetOptions.widgetType === WIDGET_TYPES.TABLE
            ? { overflowY: currentCardId === cardData.i ? 'auto' : 'hidden' }
            : titlesHeight
            ? { height: `calc(100% - ${titlesHeight + 20}px)` }
            : { backgroundColor: 'transparent' }
        }
        tabIndex={currentCardId === cardData.i ? 0 : undefined}
      >
        {renderChart()}
      </div>
      {showLoader && (
        <div className={styles.pageCardLoaderContainer}>
          <UILoader />
        </div>
      )}
    </div>
  );
};
